import { Divider } from '@chakra-ui/layout';
import { CircularProgress } from '@chakra-ui/react';
import { ResponsivePie } from '@nivo/pie';
import {
  AnalyticsIcon,
  CloseIcon,
  DescriptionIcon,
  EditPencilOverviewIcon,
  TagsOverviewIcon,
  TaskProgressIcon,
} from '@sketchnote/icons';
import { Button, Switch } from '@sketchnote/shared-components';
import cn from 'classnames';
import { useRouter } from 'next/router';
import { useCallback, useState } from 'react';
import TextareaAutosize from 'react-textarea-autosize';
import { useFileExploreStore } from 'src/components/file-explore';
import { Chip1 } from 'src/features/boards/components/Analytics/Chip';
import {
  getOverviewCompletedTasks,
  getOverviewIncompleteTasks,
  getOverviewOverdueTasks,
  groupTaskCountByStatus,
} from 'src/features/boards/components/Analytics/utils';
import { useFileStore } from 'src/features/files/store/useFileStore';
import { useTableStore } from 'src/features/list/store';

import { ProjectDetails } from './components/project-details';
export const ProjectOverview = (): JSX.Element => {
  const { closeOverviewMenu } = useFileExploreStore();
  const [daysDiff, setDaysDiff] = useState(0);

  const handleOnClose = () => {
    closeOverviewMenu();
  };

  return (
    <>
      <div className="flex justify-between items-center py-16 px-20">
        <h6 className="text-base flex items-center">Overview</h6>
        <button onClick={handleOnClose}>
          <CloseIcon className="text-gray-500 text-xs" />
        </button>
      </div>
      <div
        className="overflow-y-scroll no-scrollbar"
        style={{
          height: `calc((100vh - 100px)`,
        }}
      >
        <FileProgress daysDiff={daysDiff} />
        <Divider />
        <ProjectDetails setDaysDiff={setDaysDiff} />
        <Divider />
        <Description />
        <Divider />
        <TaskProgress />
        <Divider />
        <TaskCount />
        <Divider />
        <Analytics />
        <Divider />
        <OverviewTags />
        <Divider />
      </div>
    </>
  );
};

const Description = () => {
  const { onUpdateOverviewField } = useFileStore();
  const router = useRouter();
  const currentFile = useFileStore(
    useCallback((s) => s.files[router.query.slug as string], [router.query.slug as string])
  );

  const [descriptionText, setDescriptionText] = useState(currentFile?.overview?.description ?? '');
  const [editDesc, setEditDesc] = useState<boolean>(false);

  const updateDescription = () => {
    currentFile &&
      currentFile.id &&
      onUpdateOverviewField({
        fileId: currentFile.id,
        value: descriptionText,
        type: 'description',
      });

    setEditDesc(false);
  };

  return (
    <div className="flex flex-col px-20 my-20 w-full">
      <section className="flex items-center justify-between">
        <span className="flex items-center">
          <DescriptionIcon stroke="#292D32" />
          <label className="text-gray-500 text-gray-999 ml-8 text-md">Description</label>
        </span>
        <EditPencilOverviewIcon
          wrapperClass="justify-end text-gray-500"
          onClick={() => setEditDesc(true)}
          onBlur={() => setEditDesc(false)}
        />
      </section>
      <TextareaAutosize
        className={cn({
          'w-full max-h-80 overflow-y-scroll no-scrollbar mt-10 p-8 rounded-md': true,
          'bg-gray-100 text-gray-600': !editDesc,
          'bg-white border': editDesc,
        })}
        placeholder="Enter description"
        value={descriptionText}
        disabled={!editDesc}
        onChange={(e) => setDescriptionText(e.target.value)}
        onBlur={() => updateDescription()}
        style={{ resize: 'none' }}
      />
      <section className="flex justify-end">
        {editDesc && (
          <Button onClick={updateDescription} className="mt-10 w-60 items-center p-5">
            Update
          </Button>
        )}
      </section>
    </div>
  );
};

const OverviewTags = () => {
  const router = useRouter();
  const currentFile = useFileStore(
    useCallback((s) => s.files[router.query.slug as string], [router.query.slug as string])
  );

  return currentFile && currentFile.tags && currentFile.tags.length > 0 ? (
    <div className="flex flex-col px-20 my-20 w-full">
      <section className="flex items-center mb-20">
        <TagsOverviewIcon />
        <label className="text-gray-500 text-gray-999 ml-8 text-md">Tags</label>
      </section>
      <section className="pb-20">
        <div className="min-w-0 flex overflow-y-scroll no-scrollbar w-full">
          <div className="grid grid-cols-5 gap-y-10">
            {currentFile?.tags?.map((item) => {
              return (
                <div
                  className="px-6 mr-8 text-white rounded-md text-xs flex flex-shrink-0 py-4"
                  key={`${item.id}`}
                  style={{ lineHeight: '10px', backgroundColor: item.color }}
                >
                  <span className="inline-block my-auto pr-2 py-4">{item.name}</span>
                </div>
              );
            })}
          </div>
        </div>
      </section>
    </div>
  ) : null;
};

const Analytics = () => {
  const { columns } = useTableStore();
  const completedTasks = getOverviewCompletedTasks(columns);
  const incompleteTasks = getOverviewIncompleteTasks(columns);
  const totalTasks = Math.floor(completedTasks + incompleteTasks);
  const overDueTasks = getOverviewOverdueTasks(columns);

  return (
    <div className="flex flex-col px-20 my-20 w-full">
      <section className="flex items-center mb-15">
        <AnalyticsIcon />
        <label className="text-gray-500 text-gray-999 ml-8 text-md">Analytics</label>
      </section>
      <section className="grid grid-cols-2 gap-y-10">
        <Chip1 icon="✅" number={completedTasks} label="Completed tasks" />
        <Chip1 icon="❗️" number={overDueTasks} label="Overdue tasks" />
        <Chip1 icon="🚫" number={incompleteTasks} label="Incomplete tasks" />
        <Chip1 icon="🔢" number={totalTasks} label="Total tasks" />
      </section>
    </div>
  );
};
const TaskProgress = () => {
  const { columns } = useTableStore();
  const completedTasks = getOverviewCompletedTasks(columns);
  const incompleteTasks = getOverviewIncompleteTasks(columns);
  const totalTasks = Math.floor((completedTasks / (completedTasks + incompleteTasks)) * 100);

  const chartData = [
    {
      id: 'incomplete',
      label: 'Incomplete',
      value: incompleteTasks,
    },
    {
      id: 'complete',
      label: 'Complete',
      value: completedTasks,
    },
  ];

  const BackgroundLayer = ({ centerX, centerY }: { centerX: number; centerY: number }) => (
    <>
      <text
        x={centerX}
        y={centerY + 5}
        textAnchor="middle"
        fill="#333333"
        style={{
          fontSize: 16,
          fontWeight: 100,
        }}
      >
        {totalTasks}%
      </text>
      <text
        x={centerX + 2}
        y={centerY + 30}
        textAnchor="middle"
        fill="#33333368"
        style={{
          fontSize: 14,
          fontWeight: 200,
        }}
      >
        Completion
      </text>
    </>
  );

  return completedTasks + incompleteTasks > 0 ? (
    <div className="flex flex-col px-20 my-20 w-full">
      <section className="flex items-center">
        <TaskProgressIcon />
        <label className="text-gray-500 text-gray-999 ml-8 text-md">Task progress</label>
      </section>
      <div className="flex items-center justify-between">
        <section className="flex flex-col h-180 w-180 justify-center">
          <ResponsivePie
            data={chartData}
            colors={['#254DDA', '#FF9700', '#A650ED', '#FF4A7B']}
            margin={{ top: 0, right: 20, bottom: 0, left: 20 }}
            innerRadius={0.85}
            padAngle={0.7}
            cornerRadius={20}
            activeOuterRadiusOffset={8}
            borderWidth={0}
            borderColor={{
              from: 'color',
              modifiers: [['darker', 0.2]],
            }}
            enableArcLinkLabels={false}
            arcLabelsSkipAngle={10}
            arcLabelsTextColor={{
              from: 'color',
              modifiers: [['brighter', 10]],
            }}
            arcLabelsComponent={({ datum, label, style }) => (
              <g
                transform={JSON.stringify(style.transform).slice(1, -1)}
                style={{ pointerEvents: 'none' }}
              >
                <rect
                  fill="#ffffff"
                  stroke={datum.color}
                  strokeWidth={2}
                  x={-20}
                  y={-10}
                  width={40}
                  height={20}
                  rx={10}
                />
                <text
                  textAnchor="middle"
                  dominantBaseline="central"
                  fill={datum.color}
                  style={{
                    fontSize: 10,
                    fontWeight: 500,
                  }}
                >
                  {label}
                </text>
              </g>
            )}
            layers={['arcLinkLabels', 'arcs', 'arcLabels', 'legends', BackgroundLayer]}
            tooltip={({ datum }) => (
              <div
                style={{
                  padding: 8,
                  background: '#ffffff',
                  fontSize: '12px',
                  boxShadow: '0px 2px 8px rgba(19, 23, 32, 0.16)',
                  borderRadius: '0 0 5px 5px',
                  borderTop: `5px solid ${datum.color}`,
                }}
              >
                <p className="m-0">{datum.label}</p>
                <p style={{ fontWeight: 500 }}>{datum.value} tasks</p>
              </div>
            )}
            legends={[]}
          />
        </section>
        <section className="flex flex-col">
          <label className="text-md text-gray-999">Legends</label>
          <span className="flex mt-10 items-center">
            <div
              className="flex w-10 h-10 rounded-full mr-6 items-center"
              style={{ backgroundColor: '#FF9700' }}
            ></div>
            <label className="text-sm text-gray-999 ml-10">Task completed</label>
          </span>
          <span className="flex mt-10 items-center">
            <div className="flex w-10 h-10 rounded-full mr-6 items-center bg-primary-500 "></div>
            <label className="text-sm text-gray-999 ml-10">Task pending</label>
          </span>
        </section>
      </div>
    </div>
  ) : null;
};
const TaskCount = () => {
  const { columns } = useTableStore();
  const taskCountByStatus = groupTaskCountByStatus(columns);
  return (
    <div className="flex flex-col px-20 my-20 w-full">
      <section className="flex items-center mb-15">
        <TaskProgressIcon />
        <label className="text-gray-500 text-gray-999 ml-8 text-md">Task count by status</label>
      </section>
      <section className="grid grid-cols-4 gap-y-10">
        {columns.map((column, index) => {
          return (
            <div key={index} className="flex flex-col">
              <section className="flex items-center">
                <div
                  className="h-15 w-15 rounded-sm border mr-5"
                  style={{ backgroundColor: column.color }}
                ></div>
                <div className="text-sm max-w-100 truncate">{column.name}</div>
              </section>
              <section className="pl-20">{taskCountByStatus[column.id] ?? 0}</section>
            </div>
          );
        })}
      </section>
    </div>
  );
};

const FileProgress = ({ daysDiff }: { daysDiff: number }) => {
  const { onUpdateOverviewField } = useFileStore();
  const router = useRouter();
  const currentFile = useFileStore(
    useCallback((s) => s.files[router.query.slug as string], [router.query.slug as string])
  );
  const [fileProgress, setFileProgress] = useState(currentFile?.overview?.progress ?? 0);
  const [updateProgress, setUpdateProgress] = useState(false);
  const [isChecked, setIsChecked] = useState<boolean>(currentFile?.overview?.open ?? true);

  const updateFileProgress = (val: number) => {
    setFileProgress(val);
    setUpdateProgress(false);

    currentFile &&
      currentFile.id &&
      onUpdateOverviewField({ fileId: currentFile?.id, value: val, type: 'progress' });
  };

  return (
    <div className="flex flex-col w-full">
      <section className="flex justify-between items-center bg-gray-100 rounded-md py-6 mb-15 mx-20 px-6">
        <span className="text-sm text-gray-600">Open overview drawer when file is opened</span>
        <Switch
          className="mt-2"
          checked={isChecked}
          onChange={(e) => {
            setIsChecked(!isChecked);
            currentFile &&
              currentFile.id &&
              onUpdateOverviewField({ fileId: currentFile.id, value: !isChecked, type: 'open' });
          }}
        />
      </section>
      <Divider />
      <section className="flex flex-col items-center my-20 px-20">
        <span className="flex items-center rounded-md w-full p-6 mb-10">
          <label className="text-sm" style={{ color: '#AF1D38' }}>
            {currentFile?.overview?.start_date === undefined ||
            currentFile?.overview?.end_date === undefined
              ? `No start and end date set for ${
                  currentFile && currentFile?.name && currentFile?.name?.length > 0
                    ? currentFile?.name
                    : 'Untitled'
                }`
              : `${
                  currentFile && currentFile?.name && currentFile?.name?.length > 0
                    ? currentFile?.name
                    : 'Untitled'
                }
            is about to end in ${daysDiff} days`}
          </label>
        </span>
        <div className="flex justify-between items-center w-full">
          <div className="flex items-center max-w-200 w-200">
            <CircularProgress value={fileProgress} color="green.600" size="50px" />
            <div className="flex flex-col ml-10">
              <label className="text-gray-500 text-md">File progress</label>
              <div className={cn('text-sm text-gray-999')}>{fileProgress}% completion</div>
            </div>
          </div>
          {updateProgress && (
            <input
              type={'number'}
              max="100"
              min="0"
              value={fileProgress}
              className="border rounded-md pl-10 w-150"
              onKeyPress={(e) => {
                if (e.key === 'enter') updateFileProgress(fileProgress);
              }}
              onChange={(e) => {
                if (Number(e.target.value) < 0 || Number(e.target.value) > 100) setFileProgress(0);
                else setFileProgress(Number(e.target.value));
              }}
            />
          )}
          <Button
            status="secondary"
            className="p-auto"
            size="sm"
            onClick={() => {
              updateProgress ? updateFileProgress(fileProgress) : setUpdateProgress(true);
            }}
          >
            {updateProgress ? 'Enter' : 'Update progress'}
          </Button>
        </div>
      </section>
    </div>
  );
};
